import { useMemo, useState } from "react";
import { CornerDownRight, Search, Sparkles, X } from "lucide-react";
import { tr, uiLocale } from "@/lib/i18n";

/**
 * One post's comment thread.
 *
 * ORGANISATION (2026-09-09 rework)
 * --------------------------------
 * The old version rendered every comment — parents and replies alike —
 * as sibling <li>s in one flat `space-y-3` list, distinguished only by
 * a 20px left margin. On a post with a dozen threads that reads as an
 * undifferentiated wall: you can't see where one conversation ends and
 * the next begins, and there's no way to tell which ones you've already
 * answered.
 *
 * Now each top-level comment is a bordered card that OWNS its replies,
 * so thread boundaries are visible at a glance, and the card carries
 * the two signals an editor actually triages on:
 *
 *   - "Needs reply"  — nobody from the Page has spoken in this thread
 *   - "Your Page"    — this comment is ours, not a reader's
 *
 * `needs_reply` and `is_page_author` are computed server-side in
 * `social_comment_utils.annotate_authorship`, so they stay correct even
 * when Meta redacts reader identity.
 *
 * The JSX still avoids a self-referencing component: replies are
 * flattened into one list per card before rendering. CRA's Babel
 * transformer stack-overflows on recursive components (a known
 * interaction between the preset's static hoisting and the traverse
 * plugin), and that landmine is easy to re-arm by accident.
 */

/** Flatten a comment's descendants into one ordered list with depth. */
function flattenReplies(comment, depth = 1, chain = []) {
  const out = [];
  const nextChain = chain.concat(comment);
  for (const r of comment.replies || []) {
    out.push({ comment: r, depth, parentChain: nextChain });
    out.push(...flattenReplies(r, depth + 1, nextChain));
  }
  return out;
}

/** Case- and accent-insensitive fold — mirrors the backend's `fold`. */
function fold(s) {
  return (s || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function matches(c, q) {
  if (!q) return true;
  return fold(`${c.text || ""} ${c.author_name || ""}`).includes(q);
}

function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(uiLocale(), {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function ARButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      title={tr("Assisted Response — let the LLM draft 3 candidate replies")}
      className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono-display border border-[var(--accent)] text-[var(--accent-hover)] hover:bg-orange-50"
    >
      <Sparkles className="w-3 h-3" />{tr("AR")}</button>
  );
}

/** A single comment body — used for both parents and replies. */
function CommentBody({ c, fallbackName, isReply, testId, onOpenAR }) {
  return (
    <div
      data-testid={testId}
      className={
        "flex items-start justify-between gap-2 " +
        (c.is_page_author ? "bg-orange-50/40 border-l-2 border-[var(--accent)] pl-2 py-1 -ml-2" : "")
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          {isReply && (
            <CornerDownRight className="w-3 h-3 shrink-0 text-[var(--ink-muted)] self-center" />
          )}
          <span className="font-medium text-sm text-[var(--ink)]">
            {c.author_name || fallbackName}
          </span>
          {c.is_page_author && (
            <span className="px-1 py-0 text-[9px] uppercase tracking-wider font-mono-display bg-[var(--accent)] text-white">{tr("Your Page")}</span>
          )}
          <span className="text-[10px] font-mono-display text-[var(--ink-muted)]">
            {timeLabel(c.created_time)}
          </span>
          {c.like_count > 0 && (
            <span className="text-[10px] text-[var(--ink-muted)]">{tr("· {like_count} like{s}", { like_count: c.like_count, s: c.like_count === 1 ? "" : "s" })}
            </span>
          )}
          {c.orphan_reply && (
            <span
              className="text-[10px] text-[var(--ink-muted)] italic"
              title={tr("This is a reply — its parent comment is older than the batch Meta returned.")}
            >{tr("· reply")}</span>
          )}
        </div>
        <div className="text-sm text-[var(--ink)] mt-0.5 whitespace-pre-wrap break-words">
          {c.text || <span className="italic text-[var(--ink-muted)]">{tr("(no text — reaction only)")}</span>}
        </div>
      </div>
      <ARButton onClick={onOpenAR} />
    </div>
  );
}

export default function CommentThread({
  comments,
  postCtx,
  onOpenAR,
  postText,
  identity,
}) {
  const [query, setQuery] = useState("");
  const [order, setOrder] = useState("newest");
  const [unansweredOnly, setUnansweredOnly] = useState(false);

  const platform = postCtx?.platform;
  const fallbackName =
    platform === "instagram" ? tr("Instagram user") : tr("Facebook user");

  const q = fold(query);

  /** Sort + filter without mutating the query cache's payload. */
  const threads = useMemo(() => {
    const key = (c) => c.created_time || "";
    const sorted = [...(comments || [])].sort((a, b) =>
      order === "oldest"
        ? key(a).localeCompare(key(b))
        : key(b).localeCompare(key(a)),
    );
    return sorted.filter((c) => {
      if (unansweredOnly && c.needs_reply === false) return false;
      if (!q) return true;
      // Keep a thread when the parent OR any reply matches — dropping
      // the parent of a matching reply strips the context you searched
      // for in the first place.
      return (
        matches(c, q) ||
        flattenReplies(c).some(({ comment }) => matches(comment, q))
      );
    });
  }, [comments, order, q, unansweredOnly]);

  const totalThreads = (comments || []).length;
  const unansweredCount = (comments || []).filter(
    (c) => c.needs_reply !== false,
  ).length;

  // Meta withheld every reader name on this thread. Say so once, here,
  // rather than leaving the editor to infer it from a column of
  // identical "Facebook user" labels.
  const allRedacted =
    platform === "facebook" &&
    identity &&
    identity.total > 0 &&
    identity.named === 0;

  return (
    <div>
      {/* Thread controls — search, order, triage filter. */}
      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)]" />
          <input
            data-testid="thread-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tr("Filter these comments…")}
            className="w-full pl-7 pr-7 py-1 text-sm border border-[var(--border)] bg-white outline-none focus:border-[var(--ink)]"
          />
          {query && (
            <button
              data-testid="thread-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-muted)] hover:text-[var(--ink)]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          data-testid="thread-order"
          onClick={() => setOrder(order === "newest" ? "oldest" : "newest")}
          className="px-2 py-1 text-[10px] uppercase tracking-wider font-mono-display border border-[var(--border)] hover:border-[var(--ink)]"
        >
          {order === "newest" ? tr("Newest first") : tr("Oldest first")}
        </button>
        <button
          data-testid="thread-unanswered"
          onClick={() => setUnansweredOnly((v) => !v)}
          className={
            "px-2 py-1 text-[10px] uppercase tracking-wider font-mono-display border " +
            (unansweredOnly
              ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
              : "border-[var(--border)] hover:border-[var(--ink)]")
          }
        >{tr("Needs reply ({unansweredCount})", { unansweredCount })}</button>
      </div>

      {allRedacted && (
        <div
          data-testid="thread-identity-note"
          className="mb-3 text-[11px] text-[var(--ink-muted)] border-l-2 border-[var(--border)] pl-2"
        >{tr("Facebook withheld the commenter names on this post — see the banner above for what that means.")}</div>
      )}

      {threads.length === 0 ? (
        <div className="text-sm text-[var(--ink-muted)] italic py-6">
          {totalThreads === 0
            ? tr("No comments on this post yet.")
            : tr("No comments match those filters.")}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="comment-thread">
          {threads.map((c) => {
            const replies = flattenReplies(c);
            const visibleReplies = q
              ? replies.filter(
                  ({ comment }) => matches(comment, q) || matches(c, q),
                )
              : replies;
            return (
              <li
                key={c.id}
                data-testid={`comment-${c.id}`}
                className={
                  "border p-3 " +
                  (c.needs_reply
                    ? "border-[var(--border)]"
                    : "border-[var(--border)] bg-[var(--secondary)]/30")
                }
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {c.needs_reply ? (
                    <span className="px-1 py-0 text-[9px] uppercase tracking-wider font-mono-display border border-[var(--accent)] text-[var(--accent-hover)]">{tr("Needs reply")}</span>
                  ) : (
                    <span className="px-1 py-0 text-[9px] uppercase tracking-wider font-mono-display text-[var(--ink-muted)]">{tr("Answered")}</span>
                  )}
                  {replies.length > 0 && (
                    <span className="text-[10px] font-mono-display text-[var(--ink-muted)]">
                      {replies.length}{" "}
                      {replies.length === 1 ? tr("reply") : tr("replies")}
                    </span>
                  )}
                </div>
                <CommentBody
                  c={c}
                  fallbackName={fallbackName}
                  isReply={false}
                  testId={`comment-body-${c.id}`}
                  onOpenAR={() =>
                    onOpenAR({
                      comment: c,
                      postText,
                      postCtx,
                      parentChain: [],
                    })
                  }
                />
                {visibleReplies.length > 0 && (
                  <ul className="mt-2.5 space-y-2.5 border-l border-[var(--border)] pl-3">
                    {visibleReplies.map(({ comment: r, depth, parentChain }) => (
                      <li
                        key={r.id}
                        data-testid={`comment-${r.id}`}
                        style={{ marginLeft: `${(depth - 1) * 16}px` }}
                      >
                        <CommentBody
                          c={r}
                          fallbackName={fallbackName}
                          isReply
                          testId={`comment-body-${r.id}`}
                          onOpenAR={() =>
                            onOpenAR({
                              comment: r,
                              postText,
                              postCtx,
                              parentChain,
                            })
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
